/**
 * Field registry: contrato declarativo de cada campo del formulario de ficha.
 * El admin lo usa para renderizar su mirror web y los tests de paridad app<->admin
 * lo verifican; el motor de comparacion (`compare.ts`) lo consume. Fuente de verdad
 * de "que campos existen, con que control, opciones, orden y regla de comparacion".
 *
 * El formulario de la app (RN, escrito a mano en `app/src/components/fichas/ficha-form`)
 * es la referencia visual; este registro debe mantenerse en sync con el.
 */

import {
  CLIMA_TIPO_OPCIONES,
  COLOR_PRINCIPAL_OPCIONES,
  COLOR_SUBCATEGORIAS,
  COMPLEMENTARIOS,
  DEFECTOS,
  POTENCIAL_GUARDA,
  SERVICIO_OPCIONES,
  TEMPERATURAS,
  VINIFICACION,
  type GrupoAromaKey,
  type OpcionCatalogo,
} from './catalogs'
import type { FichaCata } from './types'

export type ControlKind =
  | 'text'
  | 'numeric-text'
  | 'textarea'
  | 'segmented' // single choice (SegmentedControl)
  | 'multi-pill' // multi choice (PillSelect multi) + "otros" libre
  | 'score-slider' // 0-100
  | 'descriptor' // DescriptorSelector (arbol aromas/sabores)
  | 'cepa' // CepaAutocomplete (variedades)

/** Como se compara un campo contra la ficha de referencia del docente. */
export type CompareKind = 'choice' | 'multichoice' | 'scale' | 'numeric' | 'none'

export type FichaSectionKey =
  | 'infoBasica'
  | 'apariencia'
  | 'nariz'
  | 'gusto'
  | 'conclusiones'

export interface FieldSpec {
  section: FichaSectionKey
  /** Path dentro de FichaCata, p.ej. 'gusto.acidezEscala', 'nariz.defectos.seleccion'. */
  path: string
  /** Label en español (tambien sirve de clave i18n). */
  labelKey: string
  control: ControlKind
  /** Referencia a catalogo/enum cuando aplica (ver `resolveOptions`). */
  optionsRef?: string
  /**
   * Rotulos de los extremos para `control: 'score-slider'` (en español; fuente
   * unica para app y admin). `min` = extremo izquierdo (valor 0), `max` = extremo
   * derecho (valor 100).
   */
  sliderLabels?: { min: string; max: string }
  /** Predicado declarativo de visibilidad (ver `isFieldVisible`). */
  visibleWhen?: string
  compare: CompareKind
  /** Orden dentro de la seccion. */
  order: number
}

/** Clave de icono por seccion (cada consumidor la mapea a su asset: la app usa
 * PNGs en `assets/icons`, el admin los mismos PNGs vendoreados). */
export type FichaSectionIcon = 'copa' | 'apariencia' | 'nariz' | 'boca' | 'conclusion'

export const FICHA_SECTIONS: readonly {
  key: FichaSectionKey
  label: string
  icon: FichaSectionIcon
}[] = [
  { key: 'infoBasica', label: 'Información básica', icon: 'copa' },
  { key: 'apariencia', label: 'Apariencia', icon: 'apariencia' },
  { key: 'nariz', label: 'Nariz', icon: 'nariz' },
  { key: 'gusto', label: 'Gusto', icon: 'boca' },
  { key: 'conclusiones', label: 'Conclusiones', icon: 'conclusion' },
]

// ---------------------------------------------------------------------------
// Field registry
// ---------------------------------------------------------------------------

export const FICHA_FIELDS: readonly FieldSpec[] = [
  // ---- Info básica (datos objetivos del vino: pre-llenables, fuera de comparación) ----
  { section: 'infoBasica', path: 'vino', labelKey: 'Nombre del vino', control: 'text', compare: 'none', order: 1 },
  { section: 'infoBasica', path: 'cosecha', labelKey: 'Cosecha', control: 'numeric-text', compare: 'none', order: 2 },
  { section: 'infoBasica', path: 'alcPorcentaje', labelKey: 'Alc %', control: 'numeric-text', compare: 'none', order: 3 },
  { section: 'infoBasica', path: 'origen', labelKey: 'Origen', control: 'text', compare: 'none', order: 4 },

  // ---- Apariencia ----
  { section: 'apariencia', path: 'apariencia.claridadScore', labelKey: 'Claridad', control: 'score-slider', sliderLabels: { min: 'Turbio', max: 'Límpido' }, compare: 'scale', order: 1 },
  { section: 'apariencia', path: 'apariencia.brilloScore', labelKey: 'Brillo', control: 'score-slider', sliderLabels: { min: 'Opaco', max: 'Brillante' }, compare: 'scale', order: 2 },
  { section: 'apariencia', path: 'apariencia.colorPrincipal', labelKey: 'Color principal', control: 'segmented', optionsRef: 'COLOR_PRINCIPAL', compare: 'choice', order: 3 },
  { section: 'apariencia', path: 'apariencia.colorInterior', labelKey: 'Color secundario', control: 'segmented', optionsRef: 'COLOR_SUBCATEGORIAS', visibleWhen: "apariencia.colorPrincipal !== ''", compare: 'choice', order: 4 },
  { section: 'apariencia', path: 'apariencia.intensidad', labelKey: 'Intensidad', control: 'segmented', optionsRef: 'INTENSIDAD_BAJA', compare: 'choice', order: 5 },
  { section: 'apariencia', path: 'apariencia.variacionBorde', labelKey: 'Variación del borde', control: 'segmented', optionsRef: 'SI_NO', compare: 'choice', order: 6 },
  { section: 'apariencia', path: 'apariencia.viscosidadScore', labelKey: 'Densidad', control: 'score-slider', sliderLabels: { min: 'Acuosa', max: 'Densa' }, compare: 'scale', order: 7 },
  { section: 'apariencia', path: 'apariencia.viscosidad', labelKey: 'Viscosidad', control: 'segmented', optionsRef: 'INTENSIDAD_BAJA', compare: 'choice', order: 8 },
  { section: 'apariencia', path: 'apariencia.otrasObservaciones', labelKey: 'Otras observaciones', control: 'textarea', compare: 'none', order: 9 },

  // ---- Nariz ----
  { section: 'nariz', path: 'nariz.limpioDefectuoso', labelKey: 'Limpio o defectuoso', control: 'segmented', optionsRef: 'LIMPIO_DEFECTUOSO', compare: 'choice', order: 1 },
  { section: 'nariz', path: 'nariz.defectos.seleccion', labelKey: 'Defectos', control: 'multi-pill', optionsRef: 'DEFECTOS', visibleWhen: "nariz.limpioDefectuoso === 'defectuoso'", compare: 'multichoice', order: 2 },
  { section: 'nariz', path: 'nariz.intensidad', labelKey: 'Intensidad', control: 'segmented', optionsRef: 'INTENSIDAD_SUAVE_LABELS', compare: 'choice', order: 3 },
  { section: 'nariz', path: 'nariz.sensacionAlcohol', labelKey: 'Sensación de alcohol', control: 'segmented', optionsRef: 'SENSACION_ALCOHOL', compare: 'choice', order: 4 },
  { section: 'nariz', path: 'nariz.aromas', labelKey: 'Aromas', control: 'descriptor', compare: 'multichoice', order: 5 },
  { section: 'nariz', path: 'nariz.condicionFruta', labelKey: 'Condición de la fruta', control: 'score-slider', sliderLabels: { min: 'Poco maduro', max: 'Muy maduro' }, visibleWhen: 'hasFrutal(nariz.aromas)', compare: 'scale', order: 6 },
  { section: 'nariz', path: 'nariz.influenciaRoble', labelKey: 'Influencia del roble', control: 'segmented', optionsRef: 'PERCEPTIBLE', compare: 'choice', order: 7 },
  { section: 'nariz', path: 'nariz.nivelMadurez', labelKey: 'Nivel de madurez', control: 'segmented', optionsRef: 'NIVEL_MADUREZ', compare: 'choice', order: 8 },

  // ---- Gusto ----
  { section: 'gusto', path: 'gusto.limpioDefectuoso', labelKey: 'Limpio o defectuoso', control: 'segmented', optionsRef: 'LIMPIO_DEFECTUOSO', compare: 'choice', order: 1 },
  { section: 'gusto', path: 'gusto.defectos.seleccion', labelKey: 'Defectos', control: 'multi-pill', optionsRef: 'DEFECTOS', visibleWhen: "gusto.limpioDefectuoso === 'defectuoso'", compare: 'multichoice', order: 2 },
  { section: 'gusto', path: 'gusto.secoDulce', labelKey: 'Seco / dulce', control: 'segmented', optionsRef: 'SECO_DULCE', compare: 'choice', order: 3 },
  { section: 'gusto', path: 'gusto.co2', labelKey: 'CO2', control: 'segmented', optionsRef: 'CO2', compare: 'choice', order: 4 },
  { section: 'gusto', path: 'gusto.co2Intensidad', labelKey: 'Intensidad de la espuma', control: 'segmented', optionsRef: 'INTENSIDAD_SUAVE', visibleWhen: "gusto.co2 === 'espumoso'", compare: 'choice', order: 5 },
  { section: 'gusto', path: 'gusto.acidezEscala', labelKey: 'Acidez', control: 'segmented', optionsRef: 'ESCALA5', compare: 'choice', order: 6 },
  { section: 'gusto', path: 'gusto.acidezComplementario', labelKey: 'Acidez · complementario', control: 'multi-pill', optionsRef: 'COMP_ACIDEZ', compare: 'multichoice', order: 7 },
  { section: 'gusto', path: 'gusto.alcoholEscala', labelKey: 'Alcohol', control: 'segmented', optionsRef: 'ESCALA5', compare: 'choice', order: 8 },
  { section: 'gusto', path: 'gusto.alcoholComplementario', labelKey: 'Alcohol · complementario', control: 'multi-pill', optionsRef: 'COMP_ALCOHOL', compare: 'multichoice', order: 9 },
  { section: 'gusto', path: 'gusto.cuerpo', labelKey: 'Cuerpo', control: 'segmented', optionsRef: 'CUERPO', compare: 'choice', order: 10 },
  { section: 'gusto', path: 'gusto.cuerpoComplementario', labelKey: 'Cuerpo · complementario', control: 'multi-pill', optionsRef: 'COMP_CUERPO', compare: 'multichoice', order: 11 },
  { section: 'gusto', path: 'gusto.taninosNivel', labelKey: 'Taninos', control: 'segmented', optionsRef: 'TANINOS_NIVEL', compare: 'choice', order: 12 },
  { section: 'gusto', path: 'gusto.taninosComplementario', labelKey: 'Taninos · complementario', control: 'multi-pill', optionsRef: 'COMP_TANINOS', compare: 'multichoice', order: 13 },
  { section: 'gusto', path: 'gusto.intensidad', labelKey: 'Intensidad', control: 'segmented', optionsRef: 'INTENSIDAD_SUAVE_LABELS', compare: 'choice', order: 14 },
  { section: 'gusto', path: 'gusto.sabores', labelKey: 'Sabores', control: 'descriptor', compare: 'multichoice', order: 15 },
  { section: 'gusto', path: 'gusto.condicionFruta', labelKey: 'Condición de la fruta', control: 'score-slider', sliderLabels: { min: 'Poco maduro', max: 'Muy maduro' }, visibleWhen: 'hasFrutal(gusto.sabores)', compare: 'scale', order: 16 },
  { section: 'gusto', path: 'gusto.confirmacionRoble', labelKey: 'Confirmación influencia del roble', control: 'segmented', optionsRef: 'PERCEPTIBLE', compare: 'choice', order: 17 },
  { section: 'gusto', path: 'gusto.nivelMadurez', labelKey: 'Nivel de madurez', control: 'segmented', optionsRef: 'NIVEL_MADUREZ', compare: 'choice', order: 18 },
  { section: 'gusto', path: 'gusto.complejidad', labelKey: 'Complejidad', control: 'segmented', optionsRef: 'COMPLEJIDAD', compare: 'choice', order: 19 },
  { section: 'gusto', path: 'gusto.persistenciaEscala', labelKey: 'Final / persistencia', control: 'segmented', optionsRef: 'PERSISTENCIA', compare: 'choice', order: 20 },

  // ---- Conclusiones ----
  { section: 'conclusiones', path: 'conclusiones.vinificacionCategoria', labelKey: 'Vinificación', control: 'segmented', optionsRef: 'VINIFICACION_CATEGORIA', compare: 'choice', order: 1 },
  { section: 'conclusiones', path: 'conclusiones.vinificacionEstilo', labelKey: 'Estilo de vinificación', control: 'multi-pill', optionsRef: 'VINIFICACION_ESTILO', visibleWhen: "conclusiones.vinificacionCategoria !== ''", compare: 'multichoice', order: 2 },
  { section: 'conclusiones', path: 'conclusiones.climaFrioCalido', labelKey: 'Clima', control: 'segmented', optionsRef: 'CLIMA_FRIO_CALIDO', compare: 'choice', order: 3 },
  { section: 'conclusiones', path: 'conclusiones.climaTipo', labelKey: 'Tipo de clima', control: 'segmented', optionsRef: 'CLIMA_TIPO', compare: 'choice', order: 4 },
  { section: 'conclusiones', path: 'conclusiones.variedadUvas', labelKey: 'Variedad de uva', control: 'cepa', compare: 'none', order: 5 },
  { section: 'conclusiones', path: 'conclusiones.apelacion', labelKey: 'Apelación', control: 'text', compare: 'none', order: 6 },
  { section: 'conclusiones', path: 'conclusiones.region', labelKey: 'Región', control: 'text', compare: 'none', order: 7 },
  { section: 'conclusiones', path: 'conclusiones.pais', labelKey: 'País', control: 'text', compare: 'none', order: 8 },
  { section: 'conclusiones', path: 'conclusiones.rangoEdad', labelKey: 'Rango de edad', control: 'text', compare: 'none', order: 9 },
  { section: 'conclusiones', path: 'conclusiones.anada', labelKey: 'Añada', control: 'numeric-text', compare: 'none', order: 10 },
  { section: 'conclusiones', path: 'conclusiones.etiquetaVino', labelKey: 'Etiqueta del vino', control: 'text', compare: 'none', order: 11 },
  { section: 'conclusiones', path: 'conclusiones.productor', labelKey: 'Posible productor', control: 'text', compare: 'none', order: 12 },
  { section: 'conclusiones', path: 'conclusiones.calidad', labelKey: 'Calidad', control: 'segmented', optionsRef: 'CALIDAD', compare: 'choice', order: 13 },
  { section: 'conclusiones', path: 'conclusiones.potencialGuarda', labelKey: 'Potencial de guarda', control: 'segmented', optionsRef: 'POTENCIAL_GUARDA', compare: 'choice', order: 14 },
  { section: 'conclusiones', path: 'conclusiones.potencialGuardaOtro', labelKey: 'Potencial de guarda (especificar)', control: 'text', visibleWhen: "conclusiones.potencialGuarda === 'otro'", compare: 'none', order: 15 },
  { section: 'conclusiones', path: 'conclusiones.temperatura', labelKey: 'Temperatura de consumo', control: 'segmented', optionsRef: 'TEMPERATURAS', compare: 'choice', order: 16 },
  { section: 'conclusiones', path: 'conclusiones.servicio', labelKey: 'Servicio', control: 'segmented', optionsRef: 'SERVICIO', compare: 'choice', order: 17 },
  { section: 'conclusiones', path: 'conclusiones.seleccionCopas', labelKey: 'Selección de copas', control: 'segmented', optionsRef: 'SELECCION_COPAS', compare: 'choice', order: 18 },
  { section: 'conclusiones', path: 'conclusiones.descripcionPlatoRecomendado', labelKey: 'Maridaje', control: 'textarea', compare: 'none', order: 19 },
  { section: 'conclusiones', path: 'conclusiones.razonMaridaje', labelKey: 'Justifica maridaje', control: 'textarea', compare: 'none', order: 20 },
]

// ---------------------------------------------------------------------------
// Catálogo de opciones (resuelto por `optionsRef`)
// ---------------------------------------------------------------------------

export interface FieldOption {
  value: string
  label: string
}

const idOpts = (xs: readonly OpcionCatalogo[]): FieldOption[] =>
  xs.map((o) => ({ value: o.id, label: o.label }))
const strOpts = (xs: readonly string[]): FieldOption[] => xs.map((v) => ({ value: v, label: v }))
const pairs = (xs: readonly [string, string][]): FieldOption[] =>
  xs.map(([value, label]) => ({ value, label }))

const STATIC_OPTIONS: Record<string, FieldOption[]> = {
  COLOR_PRINCIPAL: idOpts(COLOR_PRINCIPAL_OPCIONES),
  INTENSIDAD_BAJA: pairs([
    ['baja', 'Baja'],
    ['media', 'Media'],
    ['alta', 'Alta'],
  ]),
  SI_NO: pairs([
    ['si', 'Sí'],
    ['no', 'No'],
  ]),
  LIMPIO_DEFECTUOSO: pairs([
    ['limpio', 'Limpio'],
    ['defectuoso', 'Defectuoso'],
  ]),
  DEFECTOS: idOpts(DEFECTOS),
  INTENSIDAD_SUAVE_LABELS: pairs([
    ['baja', 'Suave'],
    ['media', 'Moderada'],
    ['alta', 'Elevada'],
  ]),
  INTENSIDAD_SUAVE: pairs([
    ['suave', 'Suave'],
    ['moderada', 'Moderada'],
    ['elevada', 'Elevada'],
  ]),
  SENSACION_ALCOHOL: pairs([
    ['integrado', 'Integrado'],
    ['moderado', 'Moderado'],
    ['elevado', 'Elevado'],
  ]),
  PERCEPTIBLE: pairs([
    ['si', 'Perceptible'],
    ['no', 'Imperceptible'],
  ]),
  NIVEL_MADUREZ: pairs([
    ['joven', 'Joven'],
    ['en-desarrollo', 'En desarrollo'],
    ['maduro', 'Maduro'],
  ]),
  SECO_DULCE: pairs([
    ['muy-seco', 'Muy seco'],
    ['seco', 'Seco'],
    ['semiseco', 'Semiseco'],
    ['dulce', 'Dulce'],
    ['muy-dulce', 'Muy dulce'],
  ]),
  CO2: pairs([
    ['tranquilo', 'Tranquilo'],
    ['espumoso', 'Espumoso'],
  ]),
  ESCALA5: pairs([
    ['baja', 'Baja'],
    ['media-menos', 'Media-'],
    ['media', 'Media'],
    ['media-mas', 'Media+'],
    ['alta', 'Alta'],
  ]),
  COMP_ACIDEZ: strOpts(COMPLEMENTARIOS.acidez),
  COMP_ALCOHOL: strOpts(COMPLEMENTARIOS.alcohol),
  COMP_CUERPO: strOpts(COMPLEMENTARIOS.cuerpo),
  COMP_TANINOS: strOpts(COMPLEMENTARIOS.taninos),
  CUERPO: pairs([
    ['suave', 'Suave'],
    ['medio', 'Medio'],
    ['completo', 'Completo'],
  ]),
  TANINOS_NIVEL: pairs([
    ['bajos', 'Bajos'],
    ['moderados', 'Moderados'],
    ['altos', 'Altos'],
  ]),
  COMPLEJIDAD: pairs([
    ['baja', 'Baja'],
    ['moderada', 'Moderada'],
    ['complejo', 'Complejo'],
  ]),
  PERSISTENCIA: pairs([
    ['corta', 'Corta'],
    ['media-menos', 'Media-'],
    ['media', 'Media'],
    ['media-mas', 'Media+'],
    ['larga', 'Larga'],
  ]),
  VINIFICACION_CATEGORIA: pairs([
    ['tranquilo', 'Vino tranquilo'],
    ['espumoso', 'Vino espumoso'],
    ['dulce', 'Vino dulce'],
  ]),
  CLIMA_FRIO_CALIDO: pairs([
    ['fresco', 'Fresco'],
    ['moderado', 'Moderado'],
    ['calido', 'Cálido'],
  ]),
  CLIMA_TIPO: idOpts(CLIMA_TIPO_OPCIONES),
  CALIDAD: pairs([
    ['simple', 'Simple'],
    ['aceptable', 'Aceptable'],
    ['bueno', 'Bueno'],
    ['muy-bueno', 'Muy bueno'],
    ['excelente', 'Excelente'],
  ]),
  POTENCIAL_GUARDA: idOpts(POTENCIAL_GUARDA),
  TEMPERATURAS: idOpts(TEMPERATURAS),
  SERVICIO: idOpts(SERVICIO_OPCIONES),
  SELECCION_COPAS: pairs([
    ['blanco-estilizada', 'Blanco estilizada'],
    ['blanco-ancha', 'Blanco ancha'],
    ['tinto-estilizada', 'Tinto estilizada'],
    ['tinto-ancha', 'Tinto ancha'],
    ['generosos', 'Fortificados'],
  ]),
}

/**
 * Resuelve las opciones de un campo single/multi-choice. Algunas dependen del
 * estado de la ficha (color secundario segun color principal; estilo segun
 * categoria de vinificacion).
 */
export function resolveOptions(field: FieldSpec, ficha?: FichaCata): FieldOption[] {
  const ref = field.optionsRef
  if (!ref) return []
  if (ref === 'COLOR_SUBCATEGORIAS') {
    const color = ficha?.apariencia.colorPrincipal
    if (!color) return []
    return idOpts(COLOR_SUBCATEGORIAS[color] ?? [])
  }
  if (ref === 'VINIFICACION_ESTILO') {
    const cat = ficha?.conclusiones.vinificacionCategoria
    if (!cat) return []
    return strOpts(VINIFICACION[cat] ?? [])
  }
  return STATIC_OPTIONS[ref] ?? []
}

/** Etiqueta de un valor unico (choice) segun el catalogo del campo. */
export function optionLabel(field: FieldSpec, value: string, ficha?: FichaCata): string {
  const opt = resolveOptions(field, ficha).find((o) => o.value === value)
  return opt?.label ?? value
}

// ---------------------------------------------------------------------------
// Visibilidad condicional
// ---------------------------------------------------------------------------

export function getByPath(obj: unknown, path: string): unknown {
  return path.split('.').reduce<unknown>((acc, key) => {
    if (acc && typeof acc === 'object') return (acc as Record<string, unknown>)[key]
    return undefined
  }, obj)
}

/** ¿La familia "Frutal" (Primarios) tiene algún descriptor seleccionado? */
function hasFrutalSelection(seleccion: unknown): boolean {
  const grupos = seleccion as
    | Record<GrupoAromaKey, Record<string, { seleccionados?: string[]; otros?: string }>>
    | undefined
  const fam = grupos?.primarios?.frutal
  if (!fam) return false
  return (fam.seleccionados?.length ?? 0) > 0 || !!fam.otros
}

/**
 * Evalúa el predicado `visibleWhen` de un campo contra una ficha. Soporta:
 *  - `path === 'valor'` / `path !== 'valor'` (incluye `path !== ''`)
 *  - `hasFrutal(path)` (slider Condición de la fruta)
 */
export function isFieldVisible(field: FieldSpec, ficha: FichaCata): boolean {
  const expr = field.visibleWhen
  if (!expr) return true

  const frutal = expr.match(/^hasFrutal\(([\w.]+)\)$/)
  if (frutal) return hasFrutalSelection(getByPath(ficha, frutal[1]!))

  const m = expr.match(/^([\w.]+)\s*(===|!==)\s*'([^']*)'$/)
  if (m) {
    const actual = getByPath(ficha, m[1]!)
    const target = m[3]!
    return m[2] === '===' ? actual === target : actual !== target
  }
  return true
}

/** Campos de una sección, en orden. */
export function fieldsForSection(section: FichaSectionKey): FieldSpec[] {
  return FICHA_FIELDS.filter((f) => f.section === section).sort((a, b) => a.order - b.order)
}
